// src/pages/Profile.js
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { 
    Loader2, Copy, Eye, EyeOff, RefreshCcw, Edit2, ShieldCheck, 
    Zap, Calendar, CreditCard, Clock, Globe, User, Mail, 
    Key, ExternalLink, Sparkles, Award, TrendingUp
} from "lucide-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";
const CURRENCY_LABELS = {
    USD: 'USD - US Dollar',
    EUR: 'EUR - Euro',
    GBP: 'GBP - British Pound',
    INR: 'INR - Indian Rupee',
    AUD: 'AUD - Australian Dollar',
    CAD: 'CAD - Canadian Dollar',
};

const Profile = () => {
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [error, setError] = useState("");
    const navigate = useNavigate();
    const [editingName, setEditingName] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showSecret, setShowSecret] = useState(false);
    const [generating, setGenerating] = useState(false);

    const token = localStorage.getItem("authToken");

    const apiSecret = useMemo(() => user?.["api-secret"] || user?.api_secret || "", [user]);

    const [editableUser, setEditableUser] = useState({
        name: "",
        currency_preference: "",
        profile_image: null
    });

    const isChanged = user
        ? editableUser.name !== user.name ||
          editableUser.currency_preference !== user.currency_preference
        : false;

    const fetchUser = async () => {
        if (!token) {
            setError("You are not logged in.");
            setLoading(false);
            return;
        }

        try {
            const res = await axios.get(`${API}/user/profile`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setUser(res.data);
            setEditableUser({
                name: res.data.name || "",
                currency_preference: res.data.currency_preference || "",
                profile_image: null,
            });
        } catch (err) {
            setError("Failed to fetch profile data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUser();
    }, []);

    const copySecret = () => {
        if (!apiSecret) return;
        navigator.clipboard.writeText(apiSecret)
            .then(() => toast.success("API secret copied to clipboard!"))
            .catch(() => toast.error("Failed to copy"));
    };

    const generateApiSecret = async () => {
        if (apiSecret) {
            toast.info("You already have an API secret.");
            return;
        }
        try {
            setGenerating(true);
            const res = await axios.put(
                `${API}/user/api-generate`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            await fetchUser();
            toast.success("API secret generated successfully!");
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "Failed to generate API secret";
            toast.error(msg);
        } finally {
            setGenerating(false);
        }
    };

    const handleSignOut = () => {
        localStorage.removeItem('authToken');
        navigate("/signin");
        setUser(null);
        toast.info("Signed out successfully!");
        window.location.reload();
    };

    const handleFileChange = async (e) => {
        const token = localStorage.getItem("authToken");
        if (!token) {
            setError("You are not logged in.");
            return;
        }
        const file = e.target.files[0];
        if (!file) return;

        setEditableUser({ ...editableUser, profile_image: file });

        const formData = new FormData();
        formData.append("profile_image", file);

        try {
            const res = await fetch(`${API}/user/profile`, {
                method: "PUT",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: formData,
            });

            if (res.ok) {
                const updatedUser = await res.json();
                setUser(updatedUser);
                toast.success("Profile image updated!");
            } else {
                const err = await res.json();
                toast.error(err.detail || "Failed to update image");
            }
        } catch (err) {
            console.error(err);
            toast.error("Something went wrong!");
        }
    };

    const handleProfileUpdate = async () => {
        setSaving(true);
        const formData = new FormData();
        formData.append("name", editableUser.name);
        formData.append("currency_preference", editableUser.currency_preference);

        try {
            const res = await fetch(`${API}/user/profile`, {
                method: "PUT",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: formData,
            });

            if (res.ok) {
                const updatedUser = await res.json();
                setUser(updatedUser);
                toast.success("Profile updated successfully!");
            } else {
                const err = await res.json();
                toast.error(err.detail || "Failed to update profile");
            }
        } catch (err) {
            console.error(err);
            toast.error("Something went wrong!");
        } finally {
            setSaving(false);
        }
    };

    if (loading)
        return (
            <div className="flex items-center justify-center h-screen bg-white">
                <div className="text-center">
                    <Loader2 className="animate-spin text-blue-600 w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4" />
                    <p className="text-gray-600 text-sm sm:text-base">Loading your profile...</p>
                </div>
            </div>
        );

    if (error)
        return (
            <div className="flex items-center justify-center min-h-screen bg-white p-4">
                <div className="text-center bg-white rounded-2xl shadow-xl border border-gray-200 p-6 sm:p-8 max-w-md w-full">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <ShieldCheck className="w-6 h-6 sm:w-8 sm:h-8 text-red-600" />
                    </div>
                    <p className="text-red-600 font-semibold text-base sm:text-lg mb-4">{error}</p>
                    <button
                        onClick={() => navigate("/signin")}
                        className="w-full px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 text-sm sm:text-base"
                    >
                        Sign In
                    </button>
                </div>
            </div>
        );

    return (
        <div className="min-h-screen bg-white pt-20 sm:pt-24 pb-12 sm:pb-16 px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
                {/* Header Card */}
                <div className="mb-6 sm:mb-8 rounded-xl sm:rounded-2xl bg-white shadow-lg border border-gray-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-600 to-purple-600 h-24 sm:h-32 md:h-40"></div>
                    <div className="px-4 sm:px-6 lg:px-8 pb-6">
                        <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-12 sm:-mt-16 md:-mt-20">
                            {/* Profile Image */}
                            <div className="relative inline-block">
                                <label htmlFor="profile_image" className="cursor-pointer group">
                                    <div className="relative">
                                        <img
                                            src={
                                                editableUser.profile_image
                                                    ? URL.createObjectURL(editableUser.profile_image)
                                                    : user.profile_image
                                                    ? `${BACKEND_URL}${user.profile_image}`
                                                    : "https://cdn-icons-png.flaticon.com/128/3177/3177440.png"
                                            }
                                            alt="Profile"
                                            className="w-20 h-20 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-full object-cover border-4 border-white shadow-xl"
                                        />
                                        <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                            <Edit2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                                        </div>
                                    </div>
                                    <input
                                        type="file"
                                        id="profile_image"
                                        name="profile_image"
                                        className="hidden"
                                        onChange={handleFileChange}
                                        accept="image/*"
                                    />
                                </label>
                            </div>

                            {/* Name and Email */}
                            <div className="flex-1 sm:ml-4 md:ml-6">
                                <div className="flex items-center gap-2 mb-2">
                                    {editingName ? (
                                        <input
                                            type="text"
                                            value={editableUser.name}
                                            onChange={(e) =>
                                                setEditableUser({ ...editableUser, name: e.target.value })
                                            }
                                            onBlur={() => setEditingName(false)}
                                            autoFocus
                                            className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 border-b-2 border-blue-500 focus:outline-none bg-transparent w-full"
                                        />
                                    ) : (
                                        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
                                            <span className="break-words">{editableUser.name}</span>
                                            <button
                                                onClick={() => setEditingName(true)}
                                                className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"
                                                title="Edit name"
                                            >
                                                <Edit2 className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                                            </button>
                                        </h1>
                                    )}
                                </div>
                                {/* EMAIL - FIXED WITH PROPER CONTRAST */}
                                <div className="flex items-center gap-2 mb-3 sm:mb-4 bg-gray-100 px-3 py-2 rounded-lg">
                                    <Mail className="w-4 h-4 text-gray-700 flex-shrink-0" />
                                    <p className="break-all text-sm sm:text-base font-medium text-gray-900">{user.email}</p>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full bg-blue-100 text-blue-700 text-xs sm:text-sm font-medium">
                                        <Award className="w-3 h-3 sm:w-4 sm:h-4" />
                                        {user.plan || "Free Plan"}
                                    </span>
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full bg-green-100 text-green-700 text-xs sm:text-sm font-medium">
                                        <Zap className="w-3 h-3 sm:w-4 sm:h-4" />
                                        {Number(user.credits || 0).toLocaleString()} Credits
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 mb-6 sm:mb-8">
                    <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4 sm:p-6 hover:shadow-lg transition-shadow">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                            </div>
                            <span className="font-semibold text-gray-900 text-sm sm:text-base">Member Since</span>
                        </div>
                        <p className="text-xl sm:text-2xl font-bold text-gray-900">
                            {new Date(user.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                year: 'numeric'
                            })}
                        </p>
                        <p className="text-xs sm:text-sm text-gray-600 mt-1">
                            {new Date(user.created_at).toLocaleDateString('en-US', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                            })}
                        </p>
                    </div>

                    <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4 sm:p-6 hover:shadow-lg transition-shadow">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                                <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                            </div>
                            <span className="font-semibold text-gray-900 text-sm sm:text-base">Current Plan</span>
                        </div>
                        <p className="text-xl sm:text-2xl font-bold text-gray-900">{user.plan || "Free"}</p>
                        <button
                            onClick={() => navigate("/pricing")}
                            className="text-xs sm:text-sm text-purple-600 hover:text-purple-700 font-medium mt-2 inline-flex items-center gap-1"
                        >
                            Upgrade Plan <ExternalLink className="w-3 h-3" />
                        </button>
                    </div>

                    <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4 sm:p-6 hover:shadow-lg transition-shadow">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                                <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                            </div>
                            <span className="font-semibold text-gray-900 text-sm sm:text-base">Credits Balance</span>
                        </div>
                        <p className="text-xl sm:text-2xl font-bold text-gray-900">
                            {Number(user.credits || 0).toLocaleString()}
                        </p>
                        <button
                            onClick={() => navigate("/pricing")}
                            className="text-xs sm:text-sm text-green-600 hover:text-green-700 font-medium mt-2 inline-flex items-center gap-1"
                        >
                            Buy Credits <ExternalLink className="w-3 h-3" />
                        </button>
                    </div>

                    <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4 sm:p-6 hover:shadow-lg transition-shadow">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                                <Globe className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
                            </div>
                            <span className="font-semibold text-gray-900 text-sm sm:text-base">Currency</span>
                        </div>
                        <select
                            value={editableUser.currency_preference}
                            onChange={(e) =>
                                setEditableUser({ ...editableUser, currency_preference: e.target.value })
                            }
                            className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 font-medium text-gray-900 text-sm sm:text-base"
                        >
                            {Object.entries(CURRENCY_LABELS).map(([code, label]) => (
                                <option key={code} value={code}>
                                    {code}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* API Credentials Card */}
                <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8">
                    <div className="flex items-center gap-3 mb-4 sm:mb-6">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center flex-shrink-0">
                            <Key className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                        </div>
                        <div>
                            <h3 className="text-lg sm:text-xl font-bold text-gray-900">API Credentials</h3>
                            <p className="text-xs sm:text-sm text-gray-600">Use these credentials to access our API</p>
                        </div>
                    </div>

                    {apiSecret ? (
                        <div className="space-y-4">
                            <div className="bg-gray-50 rounded-lg border border-gray-200 p-3 sm:p-4">
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-xs sm:text-sm font-semibold text-gray-900">API Secret Key</label>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setShowSecret((s) => !s)}
                                            className="p-1.5 sm:p-2 rounded-lg hover:bg-gray-200 transition-colors"
                                            title={showSecret ? "Hide" : "Show"}
                                        >
                                            {showSecret ? <EyeOff className="w-4 h-4 text-gray-600" /> : <Eye className="w-4 h-4 text-gray-600" />}
                                        </button>
                                        <button
                                            onClick={copySecret}
                                            className="p-1.5 sm:p-2 rounded-lg hover:bg-gray-200 transition-colors"
                                            title="Copy"
                                        >
                                            <Copy className="w-4 h-4 text-gray-600" />
                                        </button>
                                    </div>
                                </div>
                                <div className="px-3 py-2 sm:px-4 sm:py-3 bg-white rounded-lg border border-gray-300 font-mono text-xs sm:text-sm text-gray-900 break-all">
                                    {showSecret
                                        ? apiSecret
                                        : "•".repeat(Math.min(apiSecret.length, 40))
                                    }
                                </div>
                            </div>

                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4">
                                <div className="flex items-start gap-2 sm:gap-3">
                                    <ShieldCheck className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-xs sm:text-sm font-semibold text-yellow-900 mb-1">Keep your API secret secure!</p>
                                        <p className="text-xs text-yellow-800">
                                            Never share your API secret publicly or commit it to version control. 
                                            Use environment variables in production.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={() => navigate("/product/api")}
                                className="w-full px-4 py-2.5 sm:px-6 sm:py-3 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold hover:from-blue-700 hover:to-purple-700 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 text-sm sm:text-base"
                            >
                                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
                                View Full API Documentation
                                <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4" />
                            </button>
                        </div>
                    ) : (
                        <div className="text-center py-6 sm:py-8">
                            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Key className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
                            </div>
                            <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base px-4">No API secret yet. Generate one to start using our API.</p>
                            <button
                                onClick={generateApiSecret}
                                disabled={generating}
                                className="inline-flex items-center gap-2 px-4 py-2.5 sm:px-6 sm:py-3 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold hover:from-purple-700 hover:to-pink-700 disabled:opacity-60 transition-all shadow-md hover:shadow-lg text-sm sm:text-base"
                            >
                                {generating ? (
                                    <>
                                        <RefreshCcw className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                                        Generating...
                                    </>
                                ) : (
                                    <>
                                        <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
                                        Generate API Secret
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                    {isChanged && (
                        <button
                            onClick={handleProfileUpdate}
                            disabled={saving}
                            className="w-full sm:flex-1 px-4 py-2.5 sm:px-6 sm:py-3 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold hover:from-blue-700 hover:to-purple-700 disabled:opacity-60 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 text-sm sm:text-base"
                        >
                            {saving && <Loader2 className="animate-spin w-4 h-4 sm:w-5 sm:h-5" />}
                            {saving ? "Saving Changes..." : "Save Changes"}
                        </button>
                    )}
                    <button
                        onClick={handleSignOut}
                        className="w-full sm:w-auto px-4 py-2.5 sm:px-6 sm:py-3 rounded-lg bg-gradient-to-r from-red-500 to-pink-500 text-white font-semibold hover:from-red-600 hover:to-pink-600 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 text-sm sm:text-base"
                    >
                        <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
                        Sign Out
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Profile;
